import React from 'react';
import {
    ChartBarIcon,
    ClockIcon,
    ArrowTrendingUpIcon,
    ScaleIcon,
    PresentationChartLineIcon,
    ChartPieIcon,
} from '@heroicons/react/20/solid';
import gameAnalyzer from '@assets/images/screenshots/game-analyzer.png';

const features = [
    {
        name: 'Total Line Analysis',
        description: 'Compare combined scores against total lines to identify over/under trends and patterns.',
        icon: ChartBarIcon,
    },
    {
        name: 'Period-by-Period Breakdown',
        description: 'Analyze team performance across different game periods to spot scoring patterns and momentum shifts.',
        icon: ClockIcon,
    },
    {
        name: 'Spread Analysis',
        description: 'Evaluate team performance against the spread to identify consistent performers and value opportunities.',
        icon: ScaleIcon,
    },
    {
        name: 'Custom Time Splits',
        description: 'View performance metrics across different time frames to track team trends.',
        icon: ArrowTrendingUpIcon,
    },
    {
        name: 'Advanced Statistics',
        description: 'Access comprehensive stats including hit rates, average scores, and other key performance indicators.',
        icon: PresentationChartLineIcon,
    },
    {
        name: 'Performance Insights',
        description: 'Get detailed breakdowns of team performance metrics to make informed betting decisions.',
        icon: ChartPieIcon,
    },
];

export const AnalyticsFeature = () => {
    return (
        <div className="bg-white dark:bg-brand-dark-900 py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-2xl sm:text-center">
                    <h2 className="text-base/7 font-semibold text-brand-red-500 dark:text-brand-red-400">Advanced Analytics</h2>
                    <p className="mt-2 text-4xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-5xl sm:text-balance">
                        Make Data-Driven Decisions
                    </p>
                    <p className="mt-6 text-lg/8 text-gray-600 dark:text-gray-400">
                        Our comprehensive analytics platform provides deep insights into game performance, 
                        allowing you to analyze trends, compare statistics, and make informed decisions 
                        based on historical data and current performance metrics.
                    </p>
                </div>
            </div>
            <div className="relative overflow-hidden pt-16">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <img
                        src={gameAnalyzer}
                        alt="Game Analytics Dashboard"
                        className="mb-[-12%] rounded-xl shadow-2xl ring-1 ring-gray-900/10 dark:ring-white/10"
                    />
                    <div aria-hidden="true" className="relative">
                        <div className="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-white dark:from-brand-dark-900 pt-[7%]" />
                    </div>
                </div>
            </div>
            <div className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
                <dl className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base/7 text-gray-600 dark:text-gray-400 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
                    {features.map((feature) => (
                        <div key={feature.name} className="relative pl-9">
                            <dt className="inline font-semibold text-gray-900 dark:text-white">
                                <feature.icon 
                                    aria-hidden="true" 
                                    className="absolute top-1 left-1 size-5 text-brand-red-500 dark:text-brand-red-400" 
                                />
                                {feature.name}
                            </dt>{' '}
                            <dd className="inline">{feature.description}</dd>
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    );
};
